// Typescript CodeGen Prototype "Expectant Sunshine"
// Transform Version: v1.0.0
// Template Version:  v1.0.0
//
// -- DO NOT MODIFY THIS FILE! --

/* tslint:disable:no-trailing-whitespace */

import * as _ from 'lodash';
import * as Wellspring from '@greenheck/wellspring';
import * as Axon from '@jlekie/axon';
import { v4 as Uuid } from 'uuid';
import * as ParseHelpers from '@jlekie/parse-helpers';
import * as Zod from 'zod';
import * as ModelMapping from './configuration.modelMapping';
import * as ModelContract from './configuration.modelContract';
import * as ModelEntity from './configuration.modelEntity';
import * as ModelLayout from './configuration.modelLayout';
import * as ModelGrid from './configuration.modelGrid';
import * as ModelDictionary from './configuration.modelDictionary';
import * as Table from './configuration.table';

export function isCatalogModelModel(source: any): source is ICatalogModel {
    // Method Implementation: simple
    return source.modelName === 'CatalogModel';
}

export interface CatalogModelZodSchemaShape {
    name?: string;

    displayName?: string;
}

// Artifact Implementation: artifacts/zodSchemaType
export const CatalogModelZodSchema: Zod.ZodSchema<CatalogModelZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    displayName: Zod.string().optional()
}).nonstrict());

export interface ICatalogModel extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly displayName: string;

    readonly isNameDefined: boolean;

    readonly isDisplayNameDefined: boolean;
}

export class CatalogModel implements ICatalogModel {
    public static readonly modelName: string = 'CatalogModel';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'displayName' ];

    public get modelName(): string {
        // Method Implementation: simple
        return CatalogModel.modelName;
    }

    private _name: string;

    private _displayName: string;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get displayName(): string {
        // Method Implementation: simple
        return this._displayName;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set displayName(value: string) {
        // Method Implementation: simple
        this._displayName = value;
        this._isDisplayNameDefined = true;
    }

    private _isNameDefined: boolean;

    private _isDisplayNameDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isDisplayNameDefined(): boolean {
        // Method Implementation: simple
        return this._isDisplayNameDefined;
    }

    public constructor();
    public constructor(source: ICatalogModel);
    public constructor(source: Partial<Pick<CatalogModel, 'name' | 'displayName'>>);
    public constructor(source?: ICatalogModel | Partial<Pick<CatalogModel, 'name' | 'displayName'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isCatalogModelModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isDisplayNameDefined) {
                    this._displayName = source.displayName;
                    this._isDisplayNameDefined = true;
                }
                else {
                    this._displayName = '';
                    this._isDisplayNameDefined = false;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.displayName !== undefined) {
                    this._displayName = source.displayName;
                    this._isDisplayNameDefined = true;
                }
                else {
                    this._displayName = '';
                    this._isDisplayNameDefined = false;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._displayName = '';
            this._isDisplayNameDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return CatalogModel.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isDisplayNameDefined && this.displayName !== undefined)
            definedProperties.push('displayName');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'displayName':
                return this.displayName;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isDisplayNameDefined && this.displayName !== undefined) {
            if (this.displayName !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayName', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.displayName);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayName', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'displayName':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.displayName = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'displayName' cannot be null`);
                    this.displayName = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'displayName'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // displayName
        if (this.isDisplayNameDefined && this.displayName !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.displayName === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.displayName);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // displayName
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.displayName = <any>null;
                    break;
                case 1:
                    this.displayName = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): CatalogModel {
        // Method Implementation: simple
        const schema = CatalogModelZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: CatalogModelZodSchemaShape): CatalogModel {
        // Method Implementation: parseZodSchema
        return new CatalogModel({
            name: schema.name,
            displayName: schema.displayName
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            displayName: this.isDisplayNameDefined ? ((this.displayName === null ? undefined : this.displayName)) : undefined,
        };
    }

    public clone(): CatalogModel {
        // Method Implementation: clone
        return new CatalogModel({
            name: this.isNameDefined ? this.name : undefined,
            displayName: this.isDisplayNameDefined ? this.displayName : undefined
        });
    }
}

export interface IDataAccessService {
    loadMapper(name: string): Promise<ModelMapping.Mapper>;

    loadContract(name: string): Promise<ModelContract.Contract>;

    loadEntity(name: string): Promise<ModelEntity.Entity>;

    loadLayout(name: string): Promise<ModelLayout.Layout>;

    loadGrid(name: string): Promise<ModelGrid.Grid>;

    loadDisplayDictionary(name: string): Promise<ModelDictionary.DisplayDictionary>;

    loadTable(name: string): Promise<Table.Table>;
}

export interface ICatalogService {
    getMappers(): Promise<CatalogModel[]>;

    getContracts(): Promise<CatalogModel[]>;

    getEntities(): Promise<CatalogModel[]>;

    getLayouts(): Promise<CatalogModel[]>;

    getGrids(): Promise<CatalogModel[]>;

    getTables(): Promise<CatalogModel[]>;
}

export class DataAccessServiceClient implements Wellspring.IServiceClient {
    public readonly transport: Axon.IClientTransport;

    public readonly protocol: Axon.IProtocol;

    public readonly identifier?: string;

    public constructor(transport: Axon.IClientTransport, protocol: Axon.IProtocol, identifier?: string) {
        // Method Implementation: simple
        this.transport = transport;
        this.protocol = protocol;
        this.identifier = identifier;
    }

    public loadMapper(name: string): Promise<ModelMapping.Mapper>;
    public loadMapper(actionContext: Wellspring.ActionContext, name: string): Promise<ModelMapping.Mapper>;
    public async loadMapper(...args: unknown[]): Promise<ModelMapping.Mapper> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"string"}
            Zod.string()
        ]).check(args)) {
            const [ name ] = args;
        
            const activity = new Wellspring.DistributedActivity('dataAccess.loadMapper');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('loadMapper', 'utf8'));
                requestMetadata.add('service', Buffer.from('dataAccess', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(0);
                    writer.writeStringValue("87768cf2792e0d9c3355f32ffa85cb186218d2402f6effaa0bbbeb139edb6584");
        
                    if (name && name !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/string
                        if (name === null)
                        {
                            writer.writeByteValue(0);
                        }
                        else
                        {
                            writer.writeByteValue(1);
                            writer.writeStringValue(name);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<ModelMapping.Mapper>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `dataAccess[${this.identifier}].loadMapper`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                name: JSON.parse(JSON.stringify(name))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "dataAccess/loadMapper" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    let result: ModelMapping.Mapper;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new ModelMapping.Mapper();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `dataAccess[${this.identifier}].loadMapper`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        name: JSON.parse(JSON.stringify(name))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `dataAccess[${this.identifier}].loadMapper`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        name: JSON.parse(JSON.stringify(name))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public loadContract(name: string): Promise<ModelContract.Contract>;
    public loadContract(actionContext: Wellspring.ActionContext, name: string): Promise<ModelContract.Contract>;
    public async loadContract(...args: unknown[]): Promise<ModelContract.Contract> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"string"}
            Zod.string()
        ]).check(args)) {
            const [ name ] = args;
        
            const activity = new Wellspring.DistributedActivity('dataAccess.loadContract');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('loadContract', 'utf8'));
                requestMetadata.add('service', Buffer.from('dataAccess', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(1);
                    writer.writeStringValue("3b470f04868e0af49bc87acdcb216afe2e8f07a1207381dd865ef1f8a765c015");
        
                    if (name && name !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/string
                        if (name === null)
                        {
                            writer.writeByteValue(0);
                        }
                        else
                        {
                            writer.writeByteValue(1);
                            writer.writeStringValue(name);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<ModelContract.Contract>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `dataAccess[${this.identifier}].loadContract`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                name: JSON.parse(JSON.stringify(name))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "dataAccess/loadContract" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    let result: ModelContract.Contract;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new ModelContract.Contract();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `dataAccess[${this.identifier}].loadContract`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        name: JSON.parse(JSON.stringify(name))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `dataAccess[${this.identifier}].loadContract`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        name: JSON.parse(JSON.stringify(name))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public loadEntity(name: string): Promise<ModelEntity.Entity>;
    public loadEntity(actionContext: Wellspring.ActionContext, name: string): Promise<ModelEntity.Entity>;
    public async loadEntity(...args: unknown[]): Promise<ModelEntity.Entity> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"string"}
            Zod.string()
        ]).check(args)) {
            const [ name ] = args;
        
            const activity = new Wellspring.DistributedActivity('dataAccess.loadEntity');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('loadEntity', 'utf8'));
                requestMetadata.add('service', Buffer.from('dataAccess', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(2);
                    writer.writeStringValue("be7b8d41c94e98cdb2912bb64c863c607de331e84607d7903ddbff9fb8c3fe1b");
        
                    if (name && name !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/string
                        if (name === null)
                        {
                            writer.writeByteValue(0);
                        }
                        else
                        {
                            writer.writeByteValue(1);
                            writer.writeStringValue(name);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<ModelEntity.Entity>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `dataAccess[${this.identifier}].loadEntity`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                name: JSON.parse(JSON.stringify(name))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "dataAccess/loadEntity" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    let result: ModelEntity.Entity;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new ModelEntity.Entity();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `dataAccess[${this.identifier}].loadEntity`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        name: JSON.parse(JSON.stringify(name))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `dataAccess[${this.identifier}].loadEntity`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        name: JSON.parse(JSON.stringify(name))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public loadLayout(name: string): Promise<ModelLayout.Layout>;
    public loadLayout(actionContext: Wellspring.ActionContext, name: string): Promise<ModelLayout.Layout>;
    public async loadLayout(...args: unknown[]): Promise<ModelLayout.Layout> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"string"}
            Zod.string()
        ]).check(args)) {
            const [ name ] = args;
        
            const activity = new Wellspring.DistributedActivity('dataAccess.loadLayout');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('loadLayout', 'utf8'));
                requestMetadata.add('service', Buffer.from('dataAccess', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(3);
                    writer.writeStringValue("0d8aabab9feecc199d8d6f68ad6b1a02e25b8570c14dff94421006eea8a1127b");
        
                    if (name && name !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/string
                        if (name === null)
                        {
                            writer.writeByteValue(0);
                        }
                        else
                        {
                            writer.writeByteValue(1);
                            writer.writeStringValue(name);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<ModelLayout.Layout>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `dataAccess[${this.identifier}].loadLayout`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                name: JSON.parse(JSON.stringify(name))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "dataAccess/loadLayout" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    let result: ModelLayout.Layout;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new ModelLayout.Layout();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `dataAccess[${this.identifier}].loadLayout`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        name: JSON.parse(JSON.stringify(name))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `dataAccess[${this.identifier}].loadLayout`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        name: JSON.parse(JSON.stringify(name))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public loadGrid(name: string): Promise<ModelGrid.Grid>;
    public loadGrid(actionContext: Wellspring.ActionContext, name: string): Promise<ModelGrid.Grid>;
    public async loadGrid(...args: unknown[]): Promise<ModelGrid.Grid> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"string"}
            Zod.string()
        ]).check(args)) {
            const [ name ] = args;
        
            const activity = new Wellspring.DistributedActivity('dataAccess.loadGrid');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('loadGrid', 'utf8'));
                requestMetadata.add('service', Buffer.from('dataAccess', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(4);
                    writer.writeStringValue("16142bf6445db333714173c9bd223f1da3c6aa33429c993628e487d3a9e8f661");
        
                    if (name && name !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/string
                        if (name === null)
                        {
                            writer.writeByteValue(0);
                        }
                        else
                        {
                            writer.writeByteValue(1);
                            writer.writeStringValue(name);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<ModelGrid.Grid>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `dataAccess[${this.identifier}].loadGrid`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                name: JSON.parse(JSON.stringify(name))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "dataAccess/loadGrid" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    let result: ModelGrid.Grid;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new ModelGrid.Grid();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `dataAccess[${this.identifier}].loadGrid`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        name: JSON.parse(JSON.stringify(name))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `dataAccess[${this.identifier}].loadGrid`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        name: JSON.parse(JSON.stringify(name))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public loadDisplayDictionary(name: string): Promise<ModelDictionary.DisplayDictionary>;
    public loadDisplayDictionary(actionContext: Wellspring.ActionContext, name: string): Promise<ModelDictionary.DisplayDictionary>;
    public async loadDisplayDictionary(...args: unknown[]): Promise<ModelDictionary.DisplayDictionary> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"string"}
            Zod.string()
        ]).check(args)) {
            const [ name ] = args;
        
            const activity = new Wellspring.DistributedActivity('dataAccess.loadDisplayDictionary');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('loadDisplayDictionary', 'utf8'));
                requestMetadata.add('service', Buffer.from('dataAccess', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(5);
                    writer.writeStringValue("70654ee1007b11120840abee915f067eb9af9886085df1d7789b9bcac24aa3a9");
        
                    if (name && name !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/string
                        if (name === null)
                        {
                            writer.writeByteValue(0);
                        }
                        else
                        {
                            writer.writeByteValue(1);
                            writer.writeStringValue(name);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<ModelDictionary.DisplayDictionary>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `dataAccess[${this.identifier}].loadDisplayDictionary`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                name: JSON.parse(JSON.stringify(name))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "dataAccess/loadDisplayDictionary" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    let result: ModelDictionary.DisplayDictionary;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new ModelDictionary.DisplayDictionary();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `dataAccess[${this.identifier}].loadDisplayDictionary`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        name: JSON.parse(JSON.stringify(name))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `dataAccess[${this.identifier}].loadDisplayDictionary`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        name: JSON.parse(JSON.stringify(name))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public loadTable(name: string): Promise<Table.Table>;
    public loadTable(actionContext: Wellspring.ActionContext, name: string): Promise<Table.Table>;
    public async loadTable(...args: unknown[]): Promise<Table.Table> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"string"}
            Zod.string()
        ]).check(args)) {
            const [ name ] = args;
        
            const activity = new Wellspring.DistributedActivity('dataAccess.loadTable');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('loadTable', 'utf8'));
                requestMetadata.add('service', Buffer.from('dataAccess', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(6);
                    writer.writeStringValue("670aee7d343053fc4578f396af82d39999d40a27bd45b89eea1c377cf86861fd");
        
                    if (name && name !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/string
                        if (name === null)
                        {
                            writer.writeByteValue(0);
                        }
                        else
                        {
                            writer.writeByteValue(1);
                            writer.writeStringValue(name);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<Table.Table>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `dataAccess[${this.identifier}].loadTable`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                name: JSON.parse(JSON.stringify(name))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "dataAccess/loadTable" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    let result: Table.Table;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new Table.Table();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `dataAccess[${this.identifier}].loadTable`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        name: JSON.parse(JSON.stringify(name))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `dataAccess[${this.identifier}].loadTable`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        name: JSON.parse(JSON.stringify(name))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }
}

export class CatalogServiceClient implements Wellspring.IServiceClient {
    public readonly transport: Axon.IClientTransport;

    public readonly protocol: Axon.IProtocol;

    public readonly identifier?: string;

    public constructor(transport: Axon.IClientTransport, protocol: Axon.IProtocol, identifier?: string) {
        // Method Implementation: simple
        this.transport = transport;
        this.protocol = protocol;
        this.identifier = identifier;
    }

    public getMappers(): Promise<CatalogModel[]>;
    public getMappers(actionContext: Wellspring.ActionContext): Promise<CatalogModel[]>;
    public async getMappers(...args: unknown[]): Promise<CatalogModel[]> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
        ]).check(args)) {
            const [  ] = args;
        
            const activity = new Wellspring.DistributedActivity('catalog.getMappers');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('getMappers', 'utf8'));
                requestMetadata.add('service', Buffer.from('catalog', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(0);
                    writer.writeStringValue("7df72c70ecb43007d80a8ff29e0044fa5e3afc15eab82cab59c2ca42ad0fd002");
        
                });
        
                const responseData = await this.protocol.readTaggedData<CatalogModel[]>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `catalog[${this.identifier}].getMappers`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "catalog/getMappers" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    let result: CatalogModel[];
                    // Implementation: slipstream/read/array
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null arrays');
                            break;
                        case 1:
                            let item: CatalogModel;
                    
                            result = [];
                            
                            const length = reader.readIntegerValue();
                            for (let i = 0; i < length; i++) {
                                // Item Implementation: model
                                switch (reader.readByteValue())
                                {
                                    case 0:
                                        throw new Error('Cannot process null models');
                                        break;
                                    case 1:
                                        item = new CatalogModel();
                                        item.slipstreamRead(reader);
                                        break;
                                    default:
                                        throw new Error('Invalid null code');
                                }
                    
                                result.push(item);
                            }
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `catalog[${this.identifier}].getMappers`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `catalog[${this.identifier}].getMappers`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public getContracts(): Promise<CatalogModel[]>;
    public getContracts(actionContext: Wellspring.ActionContext): Promise<CatalogModel[]>;
    public async getContracts(...args: unknown[]): Promise<CatalogModel[]> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
        ]).check(args)) {
            const [  ] = args;
        
            const activity = new Wellspring.DistributedActivity('catalog.getContracts');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('getContracts', 'utf8'));
                requestMetadata.add('service', Buffer.from('catalog', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(1);
                    writer.writeStringValue("31072901c4277f75f6885784a91a801ddb566e17252d36f58d88419ad6eb04ba");
        
                });
        
                const responseData = await this.protocol.readTaggedData<CatalogModel[]>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `catalog[${this.identifier}].getContracts`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "catalog/getContracts" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    let result: CatalogModel[];
                    // Implementation: slipstream/read/array
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null arrays');
                            break;
                        case 1:
                            let item: CatalogModel;
                    
                            result = [];
                            
                            const length = reader.readIntegerValue();
                            for (let i = 0; i < length; i++) {
                                // Item Implementation: model
                                switch (reader.readByteValue())
                                {
                                    case 0:
                                        throw new Error('Cannot process null models');
                                        break;
                                    case 1:
                                        item = new CatalogModel();
                                        item.slipstreamRead(reader);
                                        break;
                                    default:
                                        throw new Error('Invalid null code');
                                }
                    
                                result.push(item);
                            }
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `catalog[${this.identifier}].getContracts`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `catalog[${this.identifier}].getContracts`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public getEntities(): Promise<CatalogModel[]>;
    public getEntities(actionContext: Wellspring.ActionContext): Promise<CatalogModel[]>;
    public async getEntities(...args: unknown[]): Promise<CatalogModel[]> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
        ]).check(args)) {
            const [  ] = args;
        
            const activity = new Wellspring.DistributedActivity('catalog.getEntities');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('getEntities', 'utf8'));
                requestMetadata.add('service', Buffer.from('catalog', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(2);
                    writer.writeStringValue("df99d1e7deff7e4bcf7f83aea4c8e7e9df6693643c480f6ba9ebadd107aa5464");
        
                });
        
                const responseData = await this.protocol.readTaggedData<CatalogModel[]>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `catalog[${this.identifier}].getEntities`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "catalog/getEntities" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    let result: CatalogModel[];
                    // Implementation: slipstream/read/array
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null arrays');
                            break;
                        case 1:
                            let item: CatalogModel;
                    
                            result = [];
                            
                            const length = reader.readIntegerValue();
                            for (let i = 0; i < length; i++) {
                                // Item Implementation: model
                                switch (reader.readByteValue())
                                {
                                    case 0:
                                        throw new Error('Cannot process null models');
                                        break;
                                    case 1:
                                        item = new CatalogModel();
                                        item.slipstreamRead(reader);
                                        break;
                                    default:
                                        throw new Error('Invalid null code');
                                }
                    
                                result.push(item);
                            }
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `catalog[${this.identifier}].getEntities`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `catalog[${this.identifier}].getEntities`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public getLayouts(): Promise<CatalogModel[]>;
    public getLayouts(actionContext: Wellspring.ActionContext): Promise<CatalogModel[]>;
    public async getLayouts(...args: unknown[]): Promise<CatalogModel[]> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
        ]).check(args)) {
            const [  ] = args;
        
            const activity = new Wellspring.DistributedActivity('catalog.getLayouts');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('getLayouts', 'utf8'));
                requestMetadata.add('service', Buffer.from('catalog', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(3);
                    writer.writeStringValue("793f68cdfde52d223e840dc6b1b475c647a146556a9429a68d14cf629d7f7284");
        
                });
        
                const responseData = await this.protocol.readTaggedData<CatalogModel[]>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `catalog[${this.identifier}].getLayouts`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "catalog/getLayouts" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    let result: CatalogModel[];
                    // Implementation: slipstream/read/array
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null arrays');
                            break;
                        case 1:
                            let item: CatalogModel;
                    
                            result = [];
                            
                            const length = reader.readIntegerValue();
                            for (let i = 0; i < length; i++) {
                                // Item Implementation: model
                                switch (reader.readByteValue())
                                {
                                    case 0:
                                        throw new Error('Cannot process null models');
                                        break;
                                    case 1:
                                        item = new CatalogModel();
                                        item.slipstreamRead(reader);
                                        break;
                                    default:
                                        throw new Error('Invalid null code');
                                }
                    
                                result.push(item);
                            }
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `catalog[${this.identifier}].getLayouts`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `catalog[${this.identifier}].getLayouts`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public getGrids(): Promise<CatalogModel[]>;
    public getGrids(actionContext: Wellspring.ActionContext): Promise<CatalogModel[]>;
    public async getGrids(...args: unknown[]): Promise<CatalogModel[]> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
        ]).check(args)) {
            const [  ] = args;
        
            const activity = new Wellspring.DistributedActivity('catalog.getGrids');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('getGrids', 'utf8'));
                requestMetadata.add('service', Buffer.from('catalog', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(4);
                    writer.writeStringValue("727570e2866ec29f63c21583668e7865ca02794f504bd472199de3dc8707f617");
        
                });
        
                const responseData = await this.protocol.readTaggedData<CatalogModel[]>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `catalog[${this.identifier}].getGrids`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "catalog/getGrids" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    let result: CatalogModel[];
                    // Implementation: slipstream/read/array
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null arrays');
                            break;
                        case 1:
                            let item: CatalogModel;
                    
                            result = [];
                            
                            const length = reader.readIntegerValue();
                            for (let i = 0; i < length; i++) {
                                // Item Implementation: model
                                switch (reader.readByteValue())
                                {
                                    case 0:
                                        throw new Error('Cannot process null models');
                                        break;
                                    case 1:
                                        item = new CatalogModel();
                                        item.slipstreamRead(reader);
                                        break;
                                    default:
                                        throw new Error('Invalid null code');
                                }
                    
                                result.push(item);
                            }
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `catalog[${this.identifier}].getGrids`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `catalog[${this.identifier}].getGrids`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public getTables(): Promise<CatalogModel[]>;
    public getTables(actionContext: Wellspring.ActionContext): Promise<CatalogModel[]>;
    public async getTables(...args: unknown[]): Promise<CatalogModel[]> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
        ]).check(args)) {
            const [  ] = args;
        
            const activity = new Wellspring.DistributedActivity('catalog.getTables');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('getTables', 'utf8'));
                requestMetadata.add('service', Buffer.from('catalog', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(5);
                    writer.writeStringValue("73ca5eface4b9b5c0736b4ce4834a55011935381574898b4fb8ee150b5358b14");
        
                });
        
                const responseData = await this.protocol.readTaggedData<CatalogModel[]>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `catalog[${this.identifier}].getTables`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "catalog/getTables" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    let result: CatalogModel[];
                    // Implementation: slipstream/read/array
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null arrays');
                            break;
                        case 1:
                            let item: CatalogModel;
                    
                            result = [];
                            
                            const length = reader.readIntegerValue();
                            for (let i = 0; i < length; i++) {
                                // Item Implementation: model
                                switch (reader.readByteValue())
                                {
                                    case 0:
                                        throw new Error('Cannot process null models');
                                        break;
                                    case 1:
                                        item = new CatalogModel();
                                        item.slipstreamRead(reader);
                                        break;
                                    default:
                                        throw new Error('Invalid null code');
                                }
                    
                                result.push(item);
                            }
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `catalog[${this.identifier}].getTables`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `catalog[${this.identifier}].getTables`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }
}

export class ModelClassFactory {
    public static createModel(modelName: string) {
        // Method Implementation: createModel
        switch (modelName) {
            case 'CatalogModel':
                return new CatalogModel();
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static createFromModel(modelName: string, source: any) {
        // Method Implementation: createFromModel
        switch (modelName) {
            case 'CatalogModel':
                return new CatalogModel(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static parseModel(modelName: string, source: any) {
        // Method Implementation: parseModel
        switch (modelName) {
            case 'CatalogModel':
                return CatalogModel.parse(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }
}
